import React, { useMemo } from 'react';
import classNames from 'classnames';
import { observer } from 'mobx-react';

import { useStyles } from 'ui/base/theme';
import { UnreachableFunction } from 'ui/base/types';
import { Step, StepLabel, Stepper } from 'ui/standard/stepper/stepper';

import { UpdateRestore, UpdateRestoreStatus } from 'services/pangolin/types';

import { themedStyles } from '../manage_updates';

import Messages from './update_restore_stepper_messages';

type UpdateRestoreStepStatus = 'disabled' | 'pending' | 'completed' | 'failed' | 'active';

type UpdateRestoreStep = 'reboot' | 'restore' | 'install' | 'switch';
const UpdateRestoreStepValues: Record<UpdateRestoreStep, number> = {
  reboot: 1,
  restore: 2,
  install: 3,
  switch: 4,
};

export type UpdateRestoreStepperProps = {
  restore?: UpdateRestore;
};
export const UpdateRestoreStepper = observer(({ restore }: UpdateRestoreStepperProps) => {
  const styles = useStyles(themedStyles);

  const step = useMemo(() => {
    if (!restore) return undefined;
    switch (restore?.terminal_status ?? restore?.status) {
      case UpdateRestoreStatus.PENDING: return 'reboot';
      case UpdateRestoreStatus.ROLLBACK: return 'restore';
      case UpdateRestoreStatus.DISPATCH: return 'restore';
      case UpdateRestoreStatus.DOWNLOAD: return 'install';
      case UpdateRestoreStatus.INSTALL: return 'install';
      case UpdateRestoreStatus.REBOOT: return 'switch';
      default: return undefined;
    }
  }, [restore?.terminal_status, restore?.status]);

  const finished = useMemo(() => {
    if (!restore) return false;
    switch (restore.status) {
      case UpdateRestoreStatus.COMPLETED: return true;
      case UpdateRestoreStatus.CANCELLED: return true;
      case UpdateRestoreStatus.SKIPPED: return true;
      case UpdateRestoreStatus.FAILED: return true;
      default: return false;
    }
  }, [restore?.status]);

  const skipped = useMemo(() => {
    if (!restore) return false;
    switch (restore.status) {
      case UpdateRestoreStatus.CANCELLED: return true;
      case UpdateRestoreStatus.SKIPPED: return true;
      default: return false;
    }
  }, [restore?.status]);

  const failed = useMemo(() => {
    if (!restore) return false;
    switch (restore.status) {
      case UpdateRestoreStatus.FAILED: return true;
      default: return false;
    }
  }, [restore?.status]);

  const stepValue = step ? UpdateRestoreStepValues[step] : 0;

  const stepStatusReboot: UpdateRestoreStepStatus = (
    stepValue < 1 ? finished ? 'disabled' : 'pending' :
    stepValue > 1 ? 'completed' : failed ? 'failed' :
    skipped ? 'disabled' : finished ? 'completed' : 'active'
  );
  const stepStatusRestore: UpdateRestoreStepStatus = (
    stepValue < 2 ? finished ? 'disabled' : 'pending' :
    stepValue > 2 ? 'completed' : failed ? 'failed' :
    skipped ? 'disabled' : finished ? 'completed' : 'active'
  );
  const stepStatusInstall: UpdateRestoreStepStatus = (
    stepValue < 3 ? finished ? 'disabled' : 'pending' :
    stepValue > 3 ? 'completed' : failed ? 'failed' :
    skipped ? 'disabled' : finished ? 'completed' : 'active'
  );
  const stepStatusSwitch: UpdateRestoreStepStatus = (
    stepValue < 4 ? finished ? 'disabled' : 'pending' :
    stepValue > 4 ? 'completed' : failed ? 'failed' :
    skipped ? 'disabled' : finished ? 'completed' : 'active'
  );

  const stepPulse = (stepStatus: UpdateRestoreStepStatus) => {
    return stepStatus === 'active';
  };

  const stepLabel = (stepStatus: UpdateRestoreStepStatus) => {
    switch (stepStatus) {
      case 'disabled': return Messages.stepLabelSkipped();
      case 'pending': return Messages.stepLabelPending();
      case 'completed': return Messages.stepLabelCompleted();
      case 'active': return Messages.stepLabelInProgress();
      case 'failed': return Messages.stepLabelFailed();
      default: return UnreachableFunction(stepStatus);
    }
  };

  return (
    <Stepper>
      <Step status={stepStatusReboot} pulse={stepPulse(stepStatusReboot)}>
        <StepLabel>
          <div className={classNames(styles.field)}>
            <div className={styles.fieldTextRight}>
              {Messages.stepNameReboot()}
              <div className={styles.fieldInfo}>
                {stepLabel(stepStatusReboot)}
              </div>
            </div>
          </div>
        </StepLabel>
      </Step>
      <Step status={stepStatusRestore} pulse={stepPulse(stepStatusRestore)}>
        <StepLabel>
          <div className={classNames(styles.field)}>
            <div className={styles.fieldTextRight}>
              {Messages.stepNameRestore()}
              <div className={styles.fieldInfo}>
                {stepLabel(stepStatusRestore)}
              </div>
            </div>
          </div>
        </StepLabel>
      </Step>
      <Step status={stepStatusInstall} pulse={stepPulse(stepStatusInstall)}>
        <StepLabel>
          <div className={classNames(styles.field)}>
            <div className={styles.fieldTextRight}>
              {Messages.stepNameInstall()}
              <div className={styles.fieldInfo}>
                {stepLabel(stepStatusInstall)}
              </div>
            </div>
          </div>
        </StepLabel>
      </Step>
      <Step status={stepStatusSwitch} pulse={stepPulse(stepStatusSwitch)}>
        <StepLabel>
          <div className={classNames(styles.field)}>
            <div className={styles.fieldTextRight}>
              {Messages.stepNameSwitch()}
              <div className={styles.fieldInfo}>
                {stepLabel(stepStatusSwitch)}
              </div>
            </div>
          </div>
        </StepLabel>
      </Step>
    </Stepper>
  );
});
